// frontend/src/store/useAuthStore.ts

/**
 * Authentication Store (Zustand)
 * 
 * Manages user authentication state
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api, { setAuthToken } from '@/lib/api';
import type { User, LoginRequest, SignupRequest, AuthResponse } from '@/types';

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  
  // Actions
  login: (credentials: LoginRequest) => Promise<void>;
  signup: (data: SignupRequest) => Promise<void>;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      isAuthenticated: false,

      login: async (credentials: LoginRequest) => {
        try {
          set({ isLoading: true });
          
          const response = await api.post<AuthResponse>('/auth/login', credentials);
          const { token, user } = response.data;

          setAuthToken(token);
          
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      signup: async (data: SignupRequest) => {
        try {
          set({ isLoading: true });
          
          await api.post('/auth/register', data);

          // No auto-login: the account must verify its email first
          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        setAuthToken(null);
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
      },

      updateUser: (userData: Partial<User>) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        }));
      },

      checkAuth: async () => {
        const token = get().token;
        
        if (!token) {
          set({ isAuthenticated: false, user: null });
          return;
        }

        try {
          // Verify token is still valid by fetching user profile
          const response = await api.get<{ user: User }>('/auth/me');
          
          set({
            user: response.data.user,
            isAuthenticated: true,
          });
        } catch (error) {
          // Token invalid - logout
          get().logout();
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
